import React from 'react';

import { PlusIcon } from '@vlabs/icons';
import { Tooltip, useModal } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import classNames from 'classnames';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { CreateDepartmentForm } from './CreateDepartmentForm';
import st from './CreateDepartmentIconButton.module.sass';
import { useCreateDepartmentTranslation } from './i18n';
import { useCreateDepartmentSubmit } from './useCreateDepartmentSubmit';
import type { UseCreateDepartmentSubmitParams } from './useCreateDepartmentSubmit';

type CreateDepartmentIconButtonProps = UseCreateDepartmentSubmitParams & Omit<IconButtonProps, 'icon' | 'onClick'>;

const displayName = 'CreateDepartmentIconButton';
const testid = buildTestid(displayName);

function CreateDepartmentIconButton({
  parentDepartmentId = undefined,
  onCreateDepartment = undefined,
  className = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: CreateDepartmentIconButtonProps): React.JSX.Element {
  const { t } = useCreateDepartmentTranslation();
  const modal = useModal({ size: 'small' });
  const handleSubmit = useCreateDepartmentSubmit({
    parentDepartmentId,
    onCreateDepartment,
    onSuccess: modal.close,
  });

  return (
    <React.Fragment>
      {modal.wrap(
        <CreateDepartmentForm
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <Tooltip
        position="bottom"
        tooltipContent={t('tooltip')}
        tooltipProps={{
          offset: 4,
          positionStrategy: 'fixed',
        }}
      >
        <IconButton
          className={classNames(st.Button, className)}
          icon={<PlusIcon />}
          kind="primary"
          onClick={modal.open}
          variant="flat"
          {...testid(dataTestId)}
          {...props}
        />
      </Tooltip>
    </React.Fragment>
  );
}

CreateDepartmentIconButton.displayName = displayName;

export { CreateDepartmentIconButton };
